import React, { useState, useCallback } from 'react';
import { connect } from 'react-redux';
import axios from 'axios';
import {
  Paper,
  Typography,
  makeStyles,
  Button,
  CircularProgress,
  Box,
  Snackbar,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Checkbox,
  Tooltip,
  IconButton,
  Collapse
} from '@material-ui/core';
import {
  GetApp as DownloadIcon,
  CloudUpload as UploadIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  FiberNew as NewIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Send as ImportIcon
} from '@material-ui/icons';
import { Alert } from '@material-ui/lab';

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
    padding: theme.spacing(3),
    backgroundColor: '#f5f5f5',
    minHeight: '100vh'
  },
  header: {
    background: 'linear-gradient(45deg, #516e76 30%, #accacd 90%)',
    color: 'white',
    padding: theme.spacing(4),
    borderRadius: theme.spacing(2),
    marginBottom: theme.spacing(3),
    boxShadow: '0 3px 5px 2px rgba(33, 203, 243, .3)'
  },
  headerTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(1)
  },
  section: {
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3),
    borderRadius: theme.spacing(1)
  },
  sectionTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(2)
  },
  stepNumber: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 32,
    height: 32,
    borderRadius: '50%',
    backgroundColor: theme.palette.primary.main,
    color: 'white',
    fontWeight: 700,
    marginRight: theme.spacing(1.5),
    fontSize: '0.9rem'
  },
  stepRow: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(1)
  },
  uploadArea: {
    border: '2px dashed #ccc',
    borderRadius: theme.spacing(1),
    padding: theme.spacing(4),
    textAlign: 'center',
    cursor: 'pointer',
    transition: 'border-color 0.2s, background-color 0.2s',
    '&:hover': {
      borderColor: theme.palette.primary.main,
      backgroundColor: '#fafafa'
    }
  },
  uploadAreaActive: {
    borderColor: theme.palette.primary.main,
    backgroundColor: '#e3f2fd'
  },
  hiddenInput: {
    display: 'none'
  },
  resultsSummary: {
    display: 'flex',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    flexWrap: 'wrap'
  },
  rowNew: {
    backgroundColor: '#f1f8e9'
  },
  rowPossible: {
    backgroundColor: '#fff8e1'
  },
  rowExact: {
    backgroundColor: '#fce4ec'
  },
  rowParseError: {
    backgroundColor: '#fff3f3'
  },
  matchChip: {
    fontSize: '0.7rem',
    height: 22
  },
  matchDetail: {
    padding: theme.spacing(1, 2),
    backgroundColor: '#fafafa',
    borderLeft: '3px solid #ff9800'
  },
  actionBar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: '#fafafa',
    borderRadius: theme.spacing(1)
  },
  filterBar: {
    display: 'flex',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
    flexWrap: 'wrap'
  }
}));

const MATCH_STATUS_CONFIG = {
  new: { label: 'New', color: '#4caf50', bgClass: 'rowNew' },
  possible: { label: 'Possible Match', color: '#ff9800', bgClass: 'rowPossible' },
  exact: { label: 'Exact Match', color: '#f44336', bgClass: 'rowExact' }
};

const CatalogImport = ({ url }) => {
  const classes = useStyles();
  const [downloading, setDownloading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [filter, setFilter] = useState('all'); // 'all', 'new', 'possible', 'exact'
  const [dragOver, setDragOver] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleDownloadTemplate = async () => {
    setDownloading(true);
    try {
      const response = await axios.get('/api/aspire/catalog/import-template', {
        responseType: 'blob'
      });

      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;

      const contentDisposition = response.headers['content-disposition'];
      let filename = `CatalogImport_Template_${new Date().toISOString().split('T')[0]}.xlsx`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?(.+)"?/);
        if (match) filename = match[1];
      }

      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);

      showSnackbar('Template downloaded successfully');
    } catch (error) {
      console.error('Download error:', error);
      showSnackbar('Failed to download template: ' + (error.response?.data?.message || error.message), 'error');
    } finally {
      setDownloading(false);
    }
  };

  const handleFileUpload = async (file) => {
    if (!file) return;

    const validTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel'
    ];
    if (!validTypes.includes(file.type) && !file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      showSnackbar('Please upload an Excel file (.xlsx or .xls)', 'error');
      return;
    }

    setUploading(true);
    setUploadResult(null);
    setSelectedItems(new Set());
    setExpandedRows(new Set());
    setFilter('all');

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post('/api/aspire/catalog/import-upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data.success) {
        const result = response.data.data;
        setUploadResult(result);

        // Auto-select all "new" items by default
        const autoSelected = new Set();
        result.items.forEach((item, idx) => {
          if (item.matchStatus === 'new') {
            autoSelected.add(idx);
          }
        });
        setSelectedItems(autoSelected);

        showSnackbar(response.data.message);
      } else {
        showSnackbar(response.data.message || 'Upload failed', 'error');
      }
    } catch (error) {
      console.error('Upload error:', error);
      showSnackbar('Failed to upload file: ' + (error.response?.data?.message || error.message), 'error');
    } finally {
      setUploading(false);
    }
  };

  const handleFileInputChange = (e) => {
    const file = e.target.files[0];
    if (file) handleFileUpload(file);
    e.target.value = '';
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFileUpload(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = () => {
    setDragOver(false);
  };

  const toggleItem = useCallback((idx) => {
    setSelectedItems(prev => {
      const next = new Set(prev);
      if (next.has(idx)) {
        next.delete(idx);
      } else {
        next.add(idx);
      }
      return next;
    });
  }, []);

  const toggleExpandRow = useCallback((idx) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      if (next.has(idx)) {
        next.delete(idx);
      } else {
        next.add(idx);
      }
      return next;
    });
  }, []);

  const getFilteredItems = () => {
    if (!uploadResult?.items) return [];
    if (filter === 'all') return uploadResult.items;
    return uploadResult.items.filter(item => item.matchStatus === filter);
  };

  const selectAllFiltered = () => {
    const filtered = getFilteredItems();
    const next = new Set(selectedItems);
    filtered.forEach((item) => {
      const idx = uploadResult.items.indexOf(item);
      next.add(idx);
    });
    setSelectedItems(next);
  };

  const deselectAllFiltered = () => {
    const filtered = getFilteredItems();
    const next = new Set(selectedItems);
    filtered.forEach((item) => {
      const idx = uploadResult.items.indexOf(item);
      next.delete(idx);
    });
    setSelectedItems(next);
  };

  const handleImportSelected = async () => {
    if (selectedItems.size === 0) {
      showSnackbar('No items selected for import', 'warning');
      return;
    }

    setImporting(true);
    try {
      const itemsToImport = uploadResult.items
        .filter((_, idx) => selectedItems.has(idx))
        .map(({ warnings, possibleMatches, matchStatus, _sourceRow, ...rest }) => rest);

      const response = await axios.post('/api/aspire/catalog/import-items', {
        items: itemsToImport
      });

      if (response.data.success) {
        showSnackbar(`Successfully imported ${response.data.imported || itemsToImport.length} items`);
      } else {
        showSnackbar(response.data.message || 'Import failed', 'error');
      }
    } catch (error) {
      console.error('Import error:', error);
      showSnackbar('Failed to import items: ' + (error.response?.data?.message || error.message), 'error');
    } finally {
      setImporting(false);
    }
  };

  const filteredItems = getFilteredItems();

  return (
    <div className={classes.root}>
      {/* Header */}
      <Paper className={classes.header} elevation={0}>
        <Typography variant="h4" className={classes.headerTitle}>
          Catalog Item Import
        </Typography>
        <Typography variant="subtitle1">
          Download a template, fill in your catalog items, and upload to review before importing
        </Typography>
      </Paper>

      {/* Step 1: Download Template */}
      <Paper className={classes.section} elevation={2}>
        <div className={classes.stepRow}>
          <span className={classes.stepNumber}>1</span>
          <Typography variant="h6" className={classes.sectionTitle} style={{ marginBottom: 0 }}>
            Download Import Template
          </Typography>
        </div>
        <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16, marginLeft: 44 }}>
          The template includes dropdown lists for Category, Item Type, Unit Types, and boolean fields.
          Fill in Sheet 1 ("Import Data") with your catalog items. Sheets 2 and 3 contain the lookup data for the dropdowns.
        </Typography>
        <Box style={{ marginLeft: 44 }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={downloading ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />}
            onClick={handleDownloadTemplate}
            disabled={downloading}
          >
            {downloading ? 'Generating...' : 'Download Template'}
          </Button>
        </Box>
      </Paper>

      {/* Step 2: Upload */}
      <Paper className={classes.section} elevation={2}>
        <div className={classes.stepRow}>
          <span className={classes.stepNumber}>2</span>
          <Typography variant="h6" className={classes.sectionTitle} style={{ marginBottom: 0 }}>
            Upload Completed Template
          </Typography>
        </div>
        <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16, marginLeft: 44 }}>
          Upload your filled-in template. We'll check for items that might already exist in Aspire and let you decide what to import.
        </Typography>

        <Box style={{ marginLeft: 44 }}>
          <input
            type="file"
            accept=".xlsx,.xls"
            id="catalog-import-upload"
            className={classes.hiddenInput}
            onChange={handleFileInputChange}
          />
          <div
            className={`${classes.uploadArea} ${dragOver ? classes.uploadAreaActive : ''}`}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onClick={() => document.getElementById('catalog-import-upload').click()}
          >
            {uploading ? (
              <Box>
                <CircularProgress size={40} />
                <Typography variant="body1" style={{ marginTop: 8 }}>Processing file and checking for existing items...</Typography>
              </Box>
            ) : (
              <Box>
                <UploadIcon style={{ fontSize: 48, color: '#999' }} />
                <Typography variant="body1" style={{ marginTop: 8 }}>
                  Drag and drop your file here, or click to browse
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  Accepts .xlsx and .xls files
                </Typography>
              </Box>
            )}
          </div>
        </Box>
      </Paper>

      {/* Step 3: Review & Select */}
      {uploadResult && (
        <Paper className={classes.section} elevation={2}>
          <div className={classes.stepRow}>
            <span className={classes.stepNumber}>3</span>
            <Typography variant="h6" className={classes.sectionTitle} style={{ marginBottom: 0 }}>
              Review & Select Items to Import
            </Typography>
          </div>

          <Box style={{ marginLeft: 44 }}>
            {/* Summary chips */}
            <div className={classes.resultsSummary}>
              <Chip
                icon={<NewIcon />}
                label={`${uploadResult.summary.newItems} new`}
                style={{ backgroundColor: '#e8f5e9', color: '#2e7d32' }}
                onClick={() => setFilter(filter === 'new' ? 'all' : 'new')}
                variant={filter === 'new' ? 'default' : 'outlined'}
              />
              <Chip
                icon={<WarningIcon />}
                label={`${uploadResult.summary.possibleMatches} possible matches`}
                style={{ backgroundColor: '#fff3e0', color: '#e65100' }}
                onClick={() => setFilter(filter === 'possible' ? 'all' : 'possible')}
                variant={filter === 'possible' ? 'default' : 'outlined'}
              />
              <Chip
                icon={<ErrorIcon />}
                label={`${uploadResult.summary.exactMatches} exact matches`}
                style={{ backgroundColor: '#fce4ec', color: '#c62828' }}
                onClick={() => setFilter(filter === 'exact' ? 'all' : 'exact')}
                variant={filter === 'exact' ? 'default' : 'outlined'}
              />
              {uploadResult.summary.unparseableRows > 0 && (
                <Chip
                  icon={<ErrorIcon />}
                  label={`${uploadResult.summary.unparseableRows} unparseable`}
                  color="secondary"
                  variant="outlined"
                />
              )}
              <Chip
                label={`${uploadResult.summary.totalItems} total`}
                variant="outlined"
                onClick={() => setFilter('all')}
              />
            </div>

            {filter !== 'all' && (
              <Typography variant="caption" color="textSecondary" style={{ marginBottom: 8, display: 'block' }}>
                Showing: {filter} items. Click the chip again or "total" to show all.
              </Typography>
            )}

            {/* Selection bar */}
            <div className={classes.filterBar}>
              <Button size="small" variant="outlined" onClick={selectAllFiltered}>
                Select All {filter !== 'all' ? `(${filter})` : ''}
              </Button>
              <Button size="small" variant="outlined" onClick={deselectAllFiltered}>
                Deselect All {filter !== 'all' ? `(${filter})` : ''}
              </Button>
              <Typography variant="body2" style={{ alignSelf: 'center', marginLeft: 8 }}>
                {selectedItems.size} of {uploadResult.items.length} selected for import
              </Typography>
            </div>

            {/* Parse errors */}
            {uploadResult.parseErrors && uploadResult.parseErrors.length > 0 && (
              <Box style={{ marginBottom: 16 }}>
                <Typography variant="subtitle2" style={{ marginBottom: 8, color: '#d32f2f' }}>
                  Unparseable rows:
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Row</strong></TableCell>
                        <TableCell><strong>Reason</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {uploadResult.parseErrors.map((err, idx) => (
                        <TableRow key={idx} className={classes.rowParseError}>
                          <TableCell>{err.row}</TableCell>
                          <TableCell>{err.reason}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <Divider style={{ margin: '16px 0' }} />
              </Box>
            )}

            {/* Items table */}
            {filteredItems.length > 0 && (
              <TableContainer style={{ maxHeight: 600 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox" style={{ width: 42 }} />
                      <TableCell style={{ width: 50 }}><strong>Row</strong></TableCell>
                      <TableCell><strong>Status</strong></TableCell>
                      <TableCell><strong>Item Name</strong></TableCell>
                      <TableCell><strong>Type</strong></TableCell>
                      <TableCell><strong>Code</strong></TableCell>
                      <TableCell><strong>Cost</strong></TableCell>
                      <TableCell style={{ width: 50 }} />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredItems.map((item) => {
                      const idx = uploadResult.items.indexOf(item);
                      const isSelected = selectedItems.has(idx);
                      const isExpanded = expandedRows.has(idx);
                      const statusConfig = MATCH_STATUS_CONFIG[item.matchStatus];
                      const hasMatches = item.possibleMatches && item.possibleMatches.length > 0;

                      return (
                        <React.Fragment key={idx}>
                          <TableRow
                            className={classes[statusConfig.bgClass]}
                            hover
                            style={{ cursor: 'pointer' }}
                            onClick={() => toggleItem(idx)}
                          >
                            <TableCell padding="checkbox">
                              <Checkbox
                                checked={isSelected}
                                color="primary"
                                size="small"
                              />
                            </TableCell>
                            <TableCell>{item._sourceRow}</TableCell>
                            <TableCell>
                              <Chip
                                label={statusConfig.label}
                                size="small"
                                className={classes.matchChip}
                                style={{ backgroundColor: statusConfig.color, color: 'white' }}
                              />
                              {item.warnings && item.warnings.length > 0 && (
                                <Tooltip title={item.warnings.join(', ')}>
                                  <WarningIcon style={{ fontSize: 16, color: '#ff9800', marginLeft: 4, verticalAlign: 'middle' }} />
                                </Tooltip>
                              )}
                            </TableCell>
                            <TableCell>{item.ItemName || '-'}</TableCell>
                            <TableCell>{item.ItemType || '-'}</TableCell>
                            <TableCell>{item.ItemCode || '-'}</TableCell>
                            <TableCell>{item.ItemCost ? `$${Number(item.ItemCost).toFixed(2)}` : '-'}</TableCell>
                            <TableCell>
                              {hasMatches && (
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleExpandRow(idx);
                                  }}
                                >
                                  {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                </IconButton>
                              )}
                            </TableCell>
                          </TableRow>

                          {/* Expanded match details */}
                          {hasMatches && isExpanded && (
                            <TableRow>
                              <TableCell colSpan={8} style={{ padding: 0 }}>
                                <Collapse in={isExpanded}>
                                  <Box className={classes.matchDetail}>
                                    <Typography variant="caption" style={{ fontWeight: 600 }}>
                                      Possible existing matches:
                                    </Typography>
                                    <Table size="small" style={{ marginTop: 4 }}>
                                      <TableHead>
                                        <TableRow>
                                          <TableCell><strong>Match %</strong></TableCell>
                                          <TableCell><strong>Matched On</strong></TableCell>
                                          <TableCell><strong>Existing Name</strong></TableCell>
                                          <TableCell><strong>Existing Alt Name</strong></TableCell>
                                          <TableCell><strong>Existing Code</strong></TableCell>
                                          <TableCell><strong>ID</strong></TableCell>
                                          <TableCell><strong>Active</strong></TableCell>
                                        </TableRow>
                                      </TableHead>
                                      <TableBody>
                                        {item.possibleMatches.map((match, mIdx) => (
                                          <TableRow key={mIdx}>
                                            <TableCell>
                                              <Chip
                                                label={`${match.score}%`}
                                                size="small"
                                                className={classes.matchChip}
                                                style={{
                                                  backgroundColor: match.score === 100 ? '#f44336' : match.score >= 80 ? '#ff9800' : '#ffc107',
                                                  color: 'white'
                                                }}
                                              />
                                            </TableCell>
                                            <TableCell>{match.matchField}</TableCell>
                                            <TableCell>{match.existingItem.ItemName}</TableCell>
                                            <TableCell>{match.existingItem.ItemAlternateName || '-'}</TableCell>
                                            <TableCell>{match.existingItem.ItemCode || '-'}</TableCell>
                                            <TableCell>{match.existingItem.CatalogItemID}</TableCell>
                                            <TableCell>{match.existingItem.Active ? 'Yes' : 'No'}</TableCell>
                                          </TableRow>
                                        ))}
                                      </TableBody>
                                    </Table>
                                  </Box>
                                </Collapse>
                              </TableCell>
                            </TableRow>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {/* Import action bar */}
            <div className={classes.actionBar}>
              <Typography variant="body2">
                <strong>{selectedItems.size}</strong> item{selectedItems.size !== 1 ? 's' : ''} selected for import
              </Typography>
              <Button
                variant="contained"
                color="primary"
                startIcon={importing ? <CircularProgress size={20} color="inherit" /> : <ImportIcon />}
                onClick={handleImportSelected}
                disabled={importing || selectedItems.size === 0}
              >
                {importing ? 'Importing...' : `Import ${selectedItems.size} Item${selectedItems.size !== 1 ? 's' : ''}`}
              </Button>
            </div>
          </Box>
        </Paper>
      )}

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

const mapStateToProps = (state) => ({
  url: state.url
});

export default connect(mapStateToProps)(CatalogImport);
